import type { Filters } from '@aubade/core/ui-kit'
import {
  makeForm,
  Form,
  DropdownMenu,
  unit,
  Paragraph,
  useForm,
} from '@aubade/core/ui-kit'
import { IconEmployees3 } from '@aubade/design'
import { useConnectedUser } from '@aubade/domain/adapters'
import { isAgency } from '@aubade/domain/components'
import { HStack, Icon, VStack } from '@chakra-ui/react'
import { compactObj } from '@nartex/stdlib'
import { useId } from 'react'

import { AgencyFilter } from '../../../components'
import { ActiveFilters } from '../Components/ActiveFilters'
import type { ConversationFilterType } from '../types'

const { Input, useWatch, useFormContext } = makeForm<
  ConversationFilterType,
  'multi' | 'internal' | 'external' | 'agencies' | 'isInternal'
>()

type ContactFilterProps = {
  filters: ConversationFilterType
  onChange: (filters: Filters) => void
  reset: () => void
}

export function FilterForm(props: ContactFilterProps) {
  const { filters, onChange, reset } = props

  // @ts-expect-error not a record
  const methods = useForm<ConversationFilterType>({
    defaultValues: filters,
  })
  const formId = useId()
  const { handleSubmit } = methods
  const onFilterChange = handleSubmit(() => {
    onChange(methods.getValues())
  })

  return (
    <Form {...methods} onSubmit={onFilterChange} id={formId}>
      <FormContent onChange={onFilterChange} reset={reset} />
    </Form>
  )
}

type FormProps = { onChange: any; reset: () => void }

function FormContent(props: FormProps) {
  const { onChange, reset } = props
  const { role } = useConnectedUser()
  const [multi, external] = useWatch(['multi', 'external'])
  const { setValue } = useFormContext()
  return (
    <VStack
      width="full"
      alignItems={'flex-start'}
      gap={unit('20')}
      marginTop={unit('20')}
    >
      <HStack gap={8} alignItems={'center'}>
        {!multi && isAgency(role) && (
          <DropdownMenu title="filters.conversation.profile">
            <Input.Checkbox
              name={'external'}
              label="filters.conversation.profileExternal"
              onChange={() => {
                setValue(`agencies`, undefined)
                onChange()
              }}
            />
            <Input.Checkbox
              name={'internal'}
              label="filters.conversation.profileInternal"
              onChange={onChange}
            />
          </DropdownMenu>
        )}
        {!external && <AgencyFilters onChange={onChange} light />}
      </HStack>
      <ActiveFilters onChange={() => onChange()} />
      <HStack
        color={multi ? 'white' : 'black.500'}
        onClick={() => {
          if (!multi) {
            setValue('isInternal', true)
          } else {
            setValue('isInternal', undefined)
          }
          setValue('multi', !multi)
          setValue('external', false)
          setValue('internal', false)
          reset()
          onChange()
        }}
        backgroundColor={multi ? 'blue.500' : 'white'}
        cursor={'pointer'}
        width="full"
        borderRadius={'20px'}
        gap={unit('10')}
        padding={unit('20')}
        border="1px solid"
        borderColor="transparent"
        _hover={{ borderColor: 'blue.500' }}
      >
        <Icon
          as={IconEmployees3}
          width={unit('40')}
          padding="9px"
          height={unit('40')}
          backgroundColor="lightGrey.500"
          borderRadius="50%"
        />
        <Paragraph
          fontWeight="bold"
          text="filters.conversation.group"
          color="inherit"
        />
      </HStack>
    </VStack>
  )
}

export function ConversationListFilter(
  props: Omit<ContactFilterProps, 'reset'>,
) {
  const { filters, onChange } = props

  // @ts-expect-error not a record
  const methods = useForm<ConversationFilterType>({
    defaultValues: filters,
  })
  const formId = useId()
  const { handleSubmit } = methods
  const onFilterChange = handleSubmit(() => {
    onChange(compactObj(methods.getValues()))
  })

  return (
    <Form {...methods} onSubmit={onFilterChange} id={formId}>
      <AgencyFilters onChange={onFilterChange} withTitle />
      <ActiveFilters onChange={onFilterChange} />
    </Form>
  )
}

function AgencyFilters(props: {
  onChange: any
  withTitle?: boolean
  light?: boolean
}) {
  const { onChange, withTitle = false, light = false } = props
  return (
    <HStack gap={unit('20')} alignItems="flex-start">
      {withTitle && <Paragraph size="sm" text="filters.conversations.title" />}
      <DropdownMenu title="filters.conversations.target" light={light}>
        <AgencyFilter onFilterChange={onChange} light={light} />
      </DropdownMenu>
    </HStack>
  )
}
